import { Link } from "@tanstack/react-router";
import { foundingNote, logNotice, type PlanLike, trialDaysLeft } from "../../lib/plan.ts";
import { pluralize } from "../../lib/time.ts";
import { BuyButton } from "../buy-button.tsx";

/**
 * The trial on the log. In its last three days, one quiet line (never in
 * night mode, so it can't nag at 3am). Once it ends unpaid, a notice that
 * takes the actions' place: the log is read-only until someone buys.
 */
export function PlanNotice({
  plan,
  now,
  night,
}: {
  plan: PlanLike | undefined;
  now: Date;
  night: boolean;
}) {
  const notice = logNotice(plan, now);
  if (!plan || notice === "none") return null;

  if (notice === "ending") {
    if (night) return null;
    const left = trialDaysLeft(plan, now) ?? 0;
    const founding = foundingNote(plan.offer);
    return (
      <p className="mt-4 text-[14px]">
        <span className="opacity-70">
          Free trial: {left === 0 ? "ends today" : `${pluralize(left, "day", "days")} left`}.
          {founding ? ` ${founding}` : null}
        </span>{" "}
        <Link to="/settings" className="underline underline-offset-2">
          Buy Babia for the family
        </Link>
      </p>
    );
  }

  return (
    <section className="mt-6 border-2 border-ink p-4" aria-label="Free trial ended">
      <h2 className="heading text-[22px] leading-tight">Your free trial ended</h2>
      <p className="mt-1.5 text-[15px]">
        The log is read-only now, and every entry is kept. Buy Babia once for the whole family to
        keep logging, for up to 10 carers.
      </p>
      {foundingNote(plan.offer) ? (
        <p className="mt-1.5 text-[14px] opacity-70">{foundingNote(plan.offer)}</p>
      ) : null}
      {plan.offer ? <BuyButton offer={plan.offer} placement="log" className="mt-3" /> : null}
    </section>
  );
}
